import React, { useEffect, useState } from "react";
import { Gift, X, Sparkles, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import confetti from "canvas-confetti";

const GiftModal = ({ isOpen, onClose }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset revealed state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setIsRevealed(false);
        setIsAnimating(false);
      }, 300);
    }
  }, [isOpen]);

  // Trigger confetti when gift is revealed
  useEffect(() => {
    if (isRevealed) {
      setIsAnimating(true);

      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min;
      }

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);

        // Since particles fall down, start a bit higher than random
        confetti(
          Object.assign({}, defaults, {
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
            colors: ["#FFC700", "#FF0080", "#00FFFF", "#7928CA"],
          })
        );
        confetti(
          Object.assign({}, defaults, {
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
            colors: ["#FFC700", "#FF0080", "#00FFFF", "#7928CA"],
          })
        );
      }, 250);

      return () => clearInterval(interval);
    }
  }, [isRevealed]);

  const handleReveal = () => {
    setIsRevealed(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 z-10 overflow-hidden transform transition-all duration-300 ${
          isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        {/* Header - Gradient background */}
        <div className="bg-gradient-to-r from-black to-gray-800 p-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-yellow-300" />
            Congratulations!
          </h3>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white transition-colors duration-200 rounded-full p-1 hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <p className="text-gray-700 mb-6 text-lg">
              You've completed all your tasks! Here's your reward:
            </p>

            {!isRevealed ? (
              <div
                className="relative mx-auto w-48 h-48 rounded-2xl flex items-center justify-center cursor-pointer group transition-all duration-300 overflow-hidden"
                onClick={handleReveal}
              >
                {/* Background with subtle animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-500"></div>

                {/* Animated rings */}
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-300">
                  <div className="absolute inset-0 border-2 border-dashed border-black rounded-2xl animate-[spin_10s_linear_infinite]"></div>
                  <div className="absolute inset-4 border-2 border-dashed border-black rounded-2xl animate-[spin_7s_linear_infinite_reverse]"></div>
                </div>

                {/* Gift icon with glow */}
                <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-300">
                  <div className="absolute -inset-10 bg-yellow-300/20 rounded-full blur-2xl group-hover:bg-yellow-300/30 transition-colors duration-300"></div>
                  <Gift className="w-20 h-20 text-black" />
                </div>

                {/* Click to reveal text */}
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <span className="text-sm font-medium text-gray-600 bg-white/80 px-3 py-1 rounded-full shadow-sm">
                    Click to reveal
                  </span>
                </div>
              </div>
            ) : (
              <div className="relative mx-auto w-48 h-48 flex flex-col items-center justify-center">
                {/* Background with gradient */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl border border-yellow-200 shadow-lg ${
                    isAnimating ? "animate-pulse" : ""
                  }`}
                ></div>

                {/* Glowing effect */}
                <div className="absolute inset-0 bg-yellow-400/20 rounded-2xl blur-xl"></div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center">
                  <div className="relative">
                    <div className="absolute -inset-3 bg-yellow-300/30 rounded-full blur-md"></div>
                    <Award className="w-20 h-20 text-yellow-500" />
                  </div>
                  <div className="mt-2 text-center">
                    <span className="font-bold text-xl bg-gradient-to-r from-yellow-600 to-amber-600 bg-clip-text text-transparent">
                      Special Reward
                    </span>
                    {/* <div className="text-sm text-gray-600 mt-1">Congratulations!</div> */}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600 mb-6">
              {isRevealed
                ? "Congratulations! Our founders will be in touch with you soon to personally inform you about your special reward."
                : "Complete all tasks to unlock special rewards and benefits."}
            </p>

            <div className="flex justify-center space-x-3">
              {isRevealed ? (
                <Button
                  onClick={onClose}
                  className="bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white px-6 py-2 rounded-md transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Done
                </Button>
              ) : (
                <Button
                  onClick={handleReveal}
                  className="bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white px-6 py-2 rounded-md transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Reveal Gift
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftModal;
